import React, { useState, useMemo, useEffect } from 'react'
import { useShoppingList } from '@talktoast/shared'
import { ShoppingListItem, STORE_SECTIONS } from '@talktoast/shared'
import { formatAmount } from '@talktoast/shared'
import { Loading, Button, Input, Card } from '@talktoast/ui'
import { ICONS, getIconSize, getIconColor } from '@talktoast/shared'

interface ShoppingListScreenProps {
  onBack?: () => void
  onNext?: () => void
}

const ShoppingListScreen: React.FC<ShoppingListScreenProps> = ({ onBack, onNext }) => {
  const {
    activeList,
    loading,
    error,
    sharing,
    toggleItemChecked,
    updateItemQuantity,
    updateShoppingListStatus,
    shareShoppingList,
    getShoppingListStats,
    groupItemsByCategory,
    clearError
  } = useShoppingList()

  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [sectionsInitialized, setSectionsInitialized] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  // Expand all non-empty sections on first load
  useEffect(() => {
    if (!sectionsInitialized && Object.keys(itemsByCategory).length > 0) {
      const sectionsWithItems = Object.keys(itemsByCategory).filter(
        key => (itemsByCategory[key]?.length ?? 0) > 0
      )
      setExpandedSections(new Set(sectionsWithItems))
      setSectionsInitialized(true)
    }
  }, [itemsByCategory, sectionsInitialized])

  // Group items by category
  const itemsByCategory = useMemo(() => {
    if (!activeList) return {}
    return groupItemsByCategory()
  }, [activeList, groupItemsByCategory])

  // Get shopping list statistics
  const stats = useMemo(() => {
    return getShoppingListStats()
  }, [getShoppingListStats])

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const handleShare = async () => {
    try {
      await shareShoppingList()
    } catch (error) {
      console.error('Error sharing shopping list:', error)
    }
  }

  const handleStatusChange = (status: 'active' | 'shopping' | 'completed') => {
    updateShoppingListStatus(status)
  }

  const handleUpdateNote = (itemId: string, note: string) => {
    updateShoppingListItem(itemId, { notes: note })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loading variant="spinner" size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="max-w-md mx-auto">
          <Card variant="outlined" className="bg-red-50 border-red-200 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <ICONS.feedback.error className={`${getIconSize('lg')} ${getIconColor('error')}`} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Shopping List Unavailable</h3>
            <p className="text-red-600 mb-6">{error}</p>
            <Button
              variant="primary"
              onClick={onBack || (() => window.history.back())}
              className="w-full"
            >
              Back to Meal Planning
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  if (!activeList) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="max-w-md mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-toast-gold/10 flex items-center justify-center">
              <ICONS.food.cart className={`${getIconSize('2xl')} ${getIconColor('primary')}`} />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Shopping List</h2>
          <p className="text-gray-600 mb-6">
            Create a meal plan to generate your shopping list automatically.
          </p>
          <Button variant="primary" size="lg" onClick={onBack || (() => window.history.back())}>
            Back to Meal Planning
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-cream">
      {/* Sticky Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" onClick={onBack || (() => window.history.back())} className="flex items-center gap-1">
              <ICONS.navigation.back className={getIconSize('sm')} />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-lg font-bold text-gray-900">
              Shopping List
            </h1>
            <button
              onClick={handleShare}
              disabled={sharing}
              className="p-2 rounded-lg text-toast-gold hover:bg-toast-gold/10 transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              <ICONS.actions.share className={getIconSize('sm')} />
              <span className="hidden sm:inline text-sm">{sharing ? 'Sharing...' : 'Share'}</span>
            </button>
          </div>

          {/* Progress bar */}
          <div className="mb-2">
            <div className="flex justify-between text-xs text-gray-600 mb-1.5">
              <span>{stats.checkedItems} of {stats.totalItems} items</span>
              <span className="font-medium">{stats.completionPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-toast-gold h-2 rounded-full transition-all duration-300"
                style={{ width: `${stats.completionPercentage}%` }}
              ></div>
            </div>
          </div>

          {/* Status buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusChange('active')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border-2 ${
                activeList.status === 'active'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
              }`}
            >
              Planning
            </button>
            <button
              onClick={() => handleStatusChange('shopping')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border-2 ${
                activeList.status === 'shopping'
                  ? 'bg-toast-gold text-white border-toast-gold'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-toast-gold'
              }`}
            >
              Shopping
            </button>
            <button
              onClick={() => handleStatusChange('completed')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border-2 ${
                activeList.status === 'completed'
                  ? 'bg-avocado-green text-white border-avocado-green'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-avocado-green'
              }`}
            >
              Complete
            </button>
          </div>
        </div>
      </header>

      {/* Shopping List Items */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {Object.entries(STORE_SECTIONS)
          .sort(([, a], [, b]) => a.order - b.order)
          .map(([sectionKey, sectionInfo]) => {
            const items = itemsByCategory[sectionKey] || []
            if (items.length === 0) return null

            const isExpanded = expandedSections.has(sectionKey)
            const checkedItems = items.filter(item => item.checked).length

            return (
              <div key={sectionKey}>
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-toast-gold transition-all shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    {/* Icon Box */}
                    <div className="w-10 h-10 rounded-lg bg-toast-gold/10 flex items-center justify-center flex-shrink-0">
                      {(() => {
                        const IconComponent = ICONS.shopping[sectionInfo.icon as keyof typeof ICONS.shopping]
                        return IconComponent ? <IconComponent className={`${getIconSize('lg')} text-toast-gold`} /> : null
                      })()}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900">{sectionInfo.label}</h3>
                      <p className="text-xs text-gray-500">
                        {checkedItems}/{items.length} checked
                      </p>
                    </div>
                  </div>
                  <ICONS.navigation.forward
                    className={`${getIconSize('md')} ${getIconColor('muted')} transition-transform ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div className="mt-2 space-y-2">
                    {items.map(item => (
                      <ShoppingListItemComponent
                        key={item.id}
                        item={item}
                        isEditing={editingItemId === item.id}
                        onToggleChecked={toggleItemChecked}
                        onUpdateQuantity={updateItemQuantity}
                        onUpdateNote={handleUpdateNote}
                        onStartEditing={() => setEditingItemId(item.id)}
                        onStopEditing={() => setEditingItemId(null)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
      </div>

      {/* Next Button */}
      {onNext && (
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="primary" size="lg" fullWidth onClick={onNext} className="flex items-center justify-center gap-2">
            <span>Continue to Shopping</span>
            <ICONS.navigation.forward className={getIconSize('sm')} />
          </Button>
        </div>
      )}

      {/* Bottom spacing for mobile navigation */}
      <div className="h-20"></div>
    </div>
  )
}

interface ShoppingListItemProps {
  item: ShoppingListItem
  isEditing: boolean
  onToggleChecked: (itemId: string, checked?: boolean) => void
  onUpdateQuantity: (itemId: string, newAmount: number) => void
  onUpdateNote: (itemId: string, note: string) => void
  onStartEditing: () => void
  onStopEditing: () => void
}

const ShoppingListItemComponent: React.FC<ShoppingListItemProps> = ({
  item,
  isEditing,
  onToggleChecked,
  onUpdateQuantity,
  onUpdateNote,
  onStartEditing,
  onStopEditing
}) => {
  const [editAmount, setEditAmount] = useState(item.amount.toString())
  const [editNote, setEditNote] = useState(item.notes || '')
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false)

  const hasRecipeBreakdown = Array.isArray(item.recipeBreakdown) && item.recipeBreakdown.length > 0

  const handleToggleBreakdown = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsBreakdownExpanded(prev => !prev)
  }

  const handleToggle = () => {
    onToggleChecked(item.id)
  }

  const handleEditClick = () => {
    setEditAmount(item.amount.toString())
    setEditNote(item.notes || '')
    onStartEditing()
  }

  const handleSave = () => {
    const newAmount = parseFloat(editAmount)
    if (!isNaN(newAmount) && newAmount > 0) {
      onUpdateQuantity(item.id, newAmount)
    }
    onUpdateNote(item.id, editNote)
    onStopEditing()
  }

  const handleCancel = () => {
    setEditAmount(item.amount.toString())
    setEditNote(item.notes || '')
    onStopEditing()
  }

  const incrementAmount = () => {
    const current = parseFloat(editAmount) || 0
    setEditAmount((current + 1).toString())
  }

  const decrementAmount = () => {
    const current = parseFloat(editAmount) || 0
    if (current > 1) {
      setEditAmount((current - 1).toString())
    }
  }

  return (
    <Card
      variant="outlined"
      padding="sm"
      className={`transition-all border-2 ${
        item.checked
          ? 'bg-gray-50 opacity-60 border-gray-200'
          : 'bg-white border-gray-200 hover:border-toast-gold'
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Checkbox - 24px touch target */}
        <button
          onClick={handleToggle}
          className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
            item.checked
              ? 'bg-avocado-green border-avocado-green'
              : 'border-gray-300 hover:border-avocado-green'
          }`}
          title={item.checked ? "Checked - won't add to cart" : "Check if you already have this"}
        >
          {item.checked && (
            <ICONS.actions.check className={`${getIconSize('xs')} text-white`} />
          )}
        </button>

        {/* Item content */}
        <div className="flex-grow min-w-0">
          <div className="flex items-start justify-between gap-2">
            {/* Item name and quantity */}
            <div className="flex-grow min-w-0">
              <span className={`font-medium ${
                item.checked ? 'line-through text-gray-500' : 'text-gray-900'
              }`}>
                {/* Display purchase guidance if available (Issue #38) */}
                {item.purchaseGuidance || `${formatAmount(item.amount)} ${item.unit}`} {item.name}
              </span>

              {/* Show total quantity needed if different from purchase guidance (Issue #38) */}
              {item.totalQuantity && item.totalQuantity !== item.purchaseGuidance && (
                <div className="text-xs text-toast-gold mt-0.5 font-medium">
                  {/^[\d¼-¾⅐-⅞]/.test(item.totalQuantity.trim())
                    ? `${item.totalQuantity} needed total`
                    : item.totalQuantity}
                </div>
              )}

              {/* Show note if exists and not editing */}
              {!isEditing && item.notes && (
                <div className="text-xs text-gray-600 mt-1 flex items-start gap-1">
                  <ICONS.actions.edit className={`${getIconSize('xs')} flex-shrink-0 mt-0.5`} />
                  <span>{item.notes}</span>
                </div>
              )}

              {/* Recipe source metadata */}
              {!isEditing && item.originalIngredients && item.originalIngredients.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {item.originalIngredients.length === 1 ? (
                    <span>Used in: {item.originalIngredients[0].recipeName}</span>
                  ) : (
                    <span>
                      Used in {item.originalIngredients.length} recipes: {
                        item.originalIngredients
                          .slice(0, 2)
                          .map(orig => orig.recipeName)
                          .join(', ')
                      }
                      {item.originalIngredients.length > 2 && ` +${item.originalIngredients.length - 2} more`}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Action buttons */}
            {!isEditing && (
              <div className="flex items-center flex-shrink-0">
                {/* Breakdown toggle (chevron) */}
                {hasRecipeBreakdown && (
                  <button
                    onClick={handleToggleBreakdown}
                    className="text-gray-400 hover:text-toast-gold p-2 transition-colors"
                    aria-label={isBreakdownExpanded ? 'Collapse recipe breakdown' : 'Expand recipe breakdown'}
                    title={isBreakdownExpanded ? 'Hide recipe breakdown' : 'Show recipe breakdown'}
                  >
                    {isBreakdownExpanded
                      ? <ICONS.navigation.collapse className={getIconSize('sm')} />
                      : <ICONS.navigation.expand className={getIconSize('sm')} />
                    }
                  </button>
                )}
                {/* Edit button (pencil icon) */}
                <button
                  onClick={handleEditClick}
                  className="text-gray-400 hover:text-gray-600 p-2 transition-colors"
                  aria-label="Edit item"
                  title="Edit quantity or add note"
                >
                  <ICONS.actions.edit className={getIconSize('sm')} />
                </button>
              </div>
            )}
          </div>

          {/* Recipe Breakdown — expandable per-recipe amounts (Issue #38, approved design) */}
          {hasRecipeBreakdown && isBreakdownExpanded && !isEditing && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Recipe Breakdown
              </p>
              <ul className="space-y-1">
                {item.recipeBreakdown!.map((entry, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 text-xs text-gray-600">
                    <span className="mt-0.5 text-toast-gold flex-shrink-0">•</span>
                    <span>{entry}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Edit mode (expanded inline) */}
          {isEditing && (
            <div className="mt-3 pt-3 border-t space-y-3">
              {/* Quantity editor */}
              <div>
                <label className="text-xs text-gray-600 block mb-1">Quantity</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={decrementAmount}
                    className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm transition-colors"
                    type="button"
                  >
                    −
                  </button>
                  <Input
                    type="number"
                    value={editAmount}
                    onChange={(e) => setEditAmount(e.target.value)}
                    className="w-20 text-center"
                    min="0.1"
                    step="0.5"
                  />
                  <button
                    onClick={incrementAmount}
                    className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm transition-colors"
                    type="button"
                  >
                    +
                  </button>
                  <span className="text-sm text-gray-600">{item.unit}</span>
                </div>
              </div>

              {/* Note input */}
              <div>
                <label className="text-xs text-gray-600 block mb-1">Note (optional)</label>
                <Input
                  placeholder="e.g., get organic, store brand ok"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" onClick={handleSave}>
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

export default ShoppingListScreen